import type { DeliverySummary } from '@aeris/contracts'
import { MONEY_SCALE, formatMoney, fromString } from '@aeris/money'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'

/**
 * O acerto do motoboy numa planilha — F189.
 *
 * O dono: *"falta mais colunas, tipo a forma de pagamento, quando pagou, sei
 * lá — tudo muito seco"*. A tela ganhou as colunas; aqui saem **essas e as que
 * nem na tela cabem**: o vendedor, a filial, quem despachou, a observação do
 * pedido e quantas baixas foram estornadas.
 *
 * Dinheiro sai como número exato (`numberValue`), nunca por `Number` — a
 * mesma regra do ADR-0016 que vale na tela.
 */

/**
 * Como pagou, numa célula — e com o valor de cada parte.
 *
 * "Dinheiro R$ 50,00 + Cartão R$ 50,00" e não "Dinheiro, Cartão": quem abre a
 * planilha para conferir o maço precisa do número, e a soma das partes é o que
 * ele confere contra o total.
 */
export const paidWith = (row: DeliverySummary): string =>
  row.settlements
    // "R$ 30,00", e não "R$ 30.00" do fio (F248): a planilha é lida por gente.
    .map(
      (part) =>
        `${part.methodName} ${formatMoney(fromString(part.amount, MONEY_SCALE))}`
    )
    .join(' + ')

/** Quem deu baixa — sem repetir o mesmo nome em cada parte. */
export const settledBy = (row: DeliverySummary): string =>
  [
    ...new Set(
      row.settlements.flatMap((part) =>
        part.byName === null ? [] : [part.byName]
      )
    ),
  ].join(', ')

export const DELIVERY_EXPORT: readonly ExportColumn<DeliverySummary>[] = [
  {
    id: 'numero',
    label: 'Nº',
    value: (row) => textValue(row.number?.toString()),
  },
  { id: 'cliente', label: 'Cliente', value: (row) => row.customerName },
  {
    id: 'bairro',
    label: 'Bairro',
    value: (row) => textValue(row.customerDistrict),
  },
  {
    id: 'motoboy',
    label: 'Motoboy',
    value: (row) => textValue(row.courierName),
  },
  { id: 'saiu', label: 'Saiu', value: (row) => dateValue(row.dispatchedAt) },
  {
    id: 'despachado_por',
    label: 'Quem despachou',
    value: (row) => textValue(row.dispatchedByName),
  },
  {
    id: 'acertado',
    label: 'Acertado',
    value: (row) => dateValue(row.settledAt),
  },
  {
    id: 'combinou',
    label: 'Combinou pagar em',
    value: (row) => textValue(row.expectedMethodName),
  },
  {
    id: 'como_pagou',
    label: 'Como pagou',
    value: (row) => textValue(paidWith(row)),
  },
  {
    id: 'quem_acertou',
    label: 'Quem acertou',
    value: (row) => textValue(settledBy(row)),
  },
  {
    id: 'estornos',
    label: 'Baixas estornadas',
    // Zero sai como zero, e não em branco: célula vazia numa coluna de
    // conferência lê como "não olhei", e esta existe justamente para ser olhada.
    value: (row) => ({ number: String(row.reversedCount) }),
  },
  {
    id: 'a_receber',
    label: 'Falta receber',
    value: (row) => numberValue(row.outstanding),
  },
  {
    id: 'total',
    label: 'Total',
    value: (row) => numberValue(row.totalAmount),
  },
  {
    id: 'vendedor',
    label: 'Vendedor',
    value: (row) => textValue(row.salespersonName),
  },
  { id: 'filial', label: 'Filial', value: (row) => textValue(row.branchName) },
  {
    id: 'observacao',
    label: 'Observação',
    value: (row) => textValue(row.notes),
  },
]
